import { FocusTrap } from '@headlessui/react'
import { useIntercom } from 'react-use-intercom'

import { isImage } from '@tribeplatform/gql-client/utils'
import { MemberAvatarPlaceholder } from '@tribeplatform/react-components/common/components/member'
import { useFirstRenderContext } from '@tribeplatform/react-components/common/ssr'
import { T } from '@tribeplatform/react-components/i18n'
import { Link } from '@tribeplatform/react-components/Link'
import {
  useAuthMember,
  useLogoutAndRedirect,
  useNetwork,
} from '@tribeplatform/react-sdk/hooks'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Text } from '@tribeplatform/react-ui-kit/Text'
import {
  convertNetworkToAppearance,
  useColorScheme,
} from '@tribeplatform/react-ui-kit/ThemeProvider'

import { RuntimeConfigs } from '../../../../../configs/runtime.js'

export const AdminProfileMenu = () => {
  const { data: network } = useNetwork()
  const { data: member } = useAuthMember()
  const avatarUrl =
    isImage(member?.profilePicture) && member?.profilePicture.urls?.thumb
  const avatarName = member?.name
  const { show } = useIntercom()

  const appearance = convertNetworkToAppearance({
    oldColors: network?.themes?.active?.tokens?.colors,
    networkThemeLightColors: network?.activeTheme?.colors?.light,
    networkThemeDarkColors: network?.activeTheme?.colors?.dark,
    networkThemeTypography: network?.activeTheme?.typography,
  })
  const canSwitchTheme =
    appearance.networkColorScheme.mode === 'user-preference'

  const { colorSchemeMode, setMode } = useColorScheme()

  const { logout } = useLogoutAndRedirect()

  const { isFirstRender } = useFirstRenderContext()
  if (isFirstRender) {
    return (
      <div className="h-full flex justify-center items-center shrink-0">
        <Avatar
          src={avatarUrl}
          name={avatarName}
          fallback={MemberAvatarPlaceholder}
        />
      </div>
    )
  }

  return (
    <div className="flex justify-center items-center">
      <Dropdown placement="top-start" className="block">
        <Dropdown.ButtonMinimal className="!rounded-avatar navbar-profile-menu">
          <Avatar
            src={avatarUrl}
            name={avatarName}
            fallback={MemberAvatarPlaceholder}
            iconSize="md"
          />
        </Dropdown.ButtonMinimal>
        <FocusTrap>
          <Dropdown.Items>
            <Dropdown.Item
              as={Link}
              href="/"
              leadingIcon={
                <SvgIcon name="arrow-left" size="md" className="me-3" />
              }
            >
              <Text variant="bodySm">
                <T defaultMessage="Back to site" id="RESERVED.BackToSite" />
              </Text>
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item
              as={Link}
              href={`${RuntimeConfigs.DASHBOARD_URL}/referral?utm_source=adminpanel&utm_campaign=userimage`}
              target="_blank"
              leadingIcon={<SvgIcon name="gift" size="md" className="me-3" />}
              trailingAddon={<SvgIcon name="link-external" size="md" />}
            >
              <Text variant="bodySm">
                <T
                  defaultMessage="Referral program"
                  id="RESERVED.ReferralProgram"
                />
              </Text>
            </Dropdown.Item>
            <Dropdown.Item
              as={Link}
              href="https://bettermode.com/hub/knowledge-base"
              target="_blank"
              leadingIcon={
                <SvgIcon name="book-open" size="md" className="me-3" />
              }
              trailingAddon={<SvgIcon name="link-external" size="md" />}
            >
              <Text variant="bodySm">
                <T defaultMessage="Knowledge base" id="RESERVED.Support.KB" />
              </Text>
            </Dropdown.Item>
            <Dropdown.Item
              as={Link}
              href="https://bettermode.com/hub/academy"
              target="_blank"
              leadingIcon={<SvgIcon name="play" size="md" className="me-3" />}
              trailingAddon={<SvgIcon name="link-external" size="md" />}
            >
              <Text variant="bodySm">
                <T defaultMessage="Academy" id="RESERVED.Academy" />
              </Text>
            </Dropdown.Item>
            <Dropdown.Item
              as={Link}
              href="https://bettermode.com/hub/feed"
              target="_blank"
              leadingIcon={<SvgIcon name="users" size="md" className="me-3" />}
              trailingAddon={<SvgIcon name="link-external" size="md" />}
            >
              <Text variant="bodySm">
                <T defaultMessage="Community" id="RESERVED.Community" />
              </Text>
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item
              leadingIcon={
                <SvgIcon name="message" size="md" className="me-3" />
              }
              onClick={() => show()}
            >
              <Text variant="bodySm">
                <T
                  defaultMessage="Contact Bettermode"
                  id="RESERVED.ContactBettermode"
                />
              </Text>
            </Dropdown.Item>
            <Dropdown.Divider />
            {canSwitchTheme && (
              <Dropdown.Item
                leadingIcon={<SvgIcon name="sun" size="md" className="me-3" />}
                onClick={() =>
                  setMode(colorSchemeMode === 'dark' ? 'light' : 'dark')
                }
              >
                <Text variant="bodySm">
                  <T defaultMessage="Light/Dark" id="RESERVED.LightDark" />
                </Text>
              </Dropdown.Item>
            )}
            <Dropdown.Item
              as={Link}
              href={RuntimeConfigs.DASHBOARD_URL}
              target="_blank"
              leadingIcon={<SvgIcon name="cube" size="md" className="me-3" />}
              trailingAddon={<SvgIcon name="link-external" size="md" />}
            >
              <Text variant="bodySm">
                <T defaultMessage="My sites" id="RESERVED.MySites" />
              </Text>
            </Dropdown.Item>
            <Dropdown.Item
              role="button"
              leadingIcon={<SvgIcon name="logout" size="md" className="me-3" />}
              onClick={() => logout()}
            >
              <Text variant="bodySm">
                <T defaultMessage="Log out" id="Generics.LogOut" />
              </Text>
            </Dropdown.Item>
          </Dropdown.Items>
        </FocusTrap>
      </Dropdown>
    </div>
  )
}
